import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckIcon } from '@heroicons/react/24/solid';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import {
  GlobeAltIcon,
  CpuChipIcon,
  RocketLaunchIcon,
  SparklesIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

const TAB_KEYS = ['websiteDev', 'webAppDev', 'seo', 'custom'];

const TAB_ICONS = {
  websiteDev: GlobeAltIcon,
  webAppDev: CpuChipIcon,
  seo: RocketLaunchIcon,
  custom: SparklesIcon,
};

export default function Pricing() {
  const { t } = useTranslation('pricing');
  const [activeTab, setActiveTab] = useState('websiteDev');
  const [isVisible, setIsVisible] = useState(false);
  const [cardsVisible, setCardsVisible] = useState(false);
  const [showScrollHint, setShowScrollHint] = useState(true);
  const [activeSlide, setActiveSlide] = useState(0);
  const getLayout = () => {
    const w = window.innerWidth;
    if (w < 790) return 'mobile';
    if (w <= 1200) return 'tablet';
    return 'desktop';
  };
  const [layout, setLayout] = useState(getLayout);
  const cardsRef = useRef(null);
  const tabsScrollRef = useRef(null);
  const sliderRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setLayout(getLayout());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const el = tabsScrollRef.current;
    if (!el) return;
    const check = () => {
      setShowScrollHint(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    };
    check();
    el.addEventListener('scroll', check);
    window.addEventListener('resize', check);
    return () => {
      el.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
    };
  }, []);

  useEffect(() => {
    setIsVisible(true);
    setTimeout(() => setCardsVisible(true), 400);
  }, []);

  const handleTabChange = (tab) => {
    setCardsVisible(false);
    setActiveSlide(0);
    setActiveTab(tab);
    if (sliderRef.current) sliderRef.current.scrollLeft = 0;
    setTimeout(() => setCardsVisible(true), 50);
    if (layout === 'mobile' && cardsRef.current) {
      cardsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleSliderScroll = () => {
    const el = sliderRef.current;
    if (!el) return;
    const cardWidth = el.children[0]?.offsetWidth ?? el.clientWidth;
    setActiveSlide(Math.round(el.scrollLeft / (cardWidth + 16)));
  };

  const packages = activeTab !== 'custom' ? t(`${activeTab}.packages`, { returnObjects: true }) : [];

  return (
    <div className="min-h-screen bg-gray-50">

      {/* Hero — title & description only, no tabs */}
      <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 overflow-hidden pt-32 pb-16 px-6">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-secondary/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:50px_50px]"></div>

        <div className={`relative max-w-3xl mx-auto text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm text-gray-300 rounded-full text-sm font-semibold mb-6 border border-white/20">
            {t('hero.badge')}
          </span>
          <h1 className="text-5xl sm:text-6xl font-bold text-white mb-6">
            {t('hero.title')}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-secondary to-primary bg-[length:200%_auto] animate-gradient">
              {t('hero.titleHighlight')}
            </span>
          </h1>
          <p className="text-lg text-gray-400 leading-relaxed max-w-2xl mx-auto">
            {t('hero.description')}
          </p>
        </div>
      </div>

      {/* Sticky tabs bar — sticks below navbar (h-20 = 80px) on scroll */}
      <div className="sticky top-20 z-40 bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4">
          {/* Wrapper: relative so the scroll-hint arrow can be positioned over it */}
          <div className="relative">
            <div ref={tabsScrollRef} className="flex gap-2 overflow-x-auto scrollbar-hide py-3 md:justify-center pr-10 md:pr-0">
              {TAB_KEYS.map((tab) => {
                const Icon = TAB_ICONS[tab];
                return (
                  <button
                    key={tab}
                    onClick={() => handleTabChange(tab)}
                    className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-all duration-300 ${
                      activeTab === tab
                        ? 'bg-gradient-to-r from-primary to-secondary text-white shadow-md shadow-primary/20'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900'
                    }`}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    <span className="whitespace-nowrap">{t(`tabs.${tab}`)}</span>
                  </button>
                );
              })}
            </div>

            {/* Scroll-hint arrow — only on mobile, fades out when scrolled to end */}
            <div className={`md:hidden pointer-events-none absolute right-0 top-0 bottom-0 flex items-center transition-opacity duration-300 ${showScrollHint ? 'opacity-100' : 'opacity-0'}`}>
              {/* Gradient fade */}
              <div className="w-12 h-full bg-gradient-to-l from-white/95 to-transparent"></div>
              {/* Animated chevron */}
              <div className="absolute right-1 animate-nudge-x">
                <ChevronRightIcon className="w-5 h-5 text-primary" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Cards Section */}
      <div ref={cardsRef} className="py-16 scroll-mt-36">
        {activeTab === 'custom' ? (
          <div className="px-6 sm:px-12">
            <div className="max-w-7xl mx-auto">
              <CustomCard t={t} isVisible={cardsVisible} />
            </div>
          </div>
        ) : layout === 'mobile' ? (
          /* ── Mobile: snap slider ── */
          <div>
            <div
              ref={sliderRef}
              onScroll={handleSliderScroll}
              style={{
                display: 'flex',
                alignItems: 'stretch',
                overflowX: 'auto',
                scrollSnapType: 'x mandatory',
                WebkitOverflowScrolling: 'touch',
                gap: '16px',
                paddingLeft: '20px',
                paddingBottom: '16px',
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
              }}
            >
              {packages.map((pkg, index) => (
                <div
                  key={`${activeTab}-${index}`}
                  style={{ flexShrink: 0, width: '82vw', scrollSnapAlign: 'start' }}
                >
                  <PricingCard pkg={pkg} isVisible={cardsVisible} delay={index * 80} t={t} />
                </div>
              ))}
              <div style={{ flexShrink: 0, width: '20px' }} />
            </div>

            {/* Dot indicators */}
            <div className="flex justify-center gap-2 mt-5">
              {packages.map((_, i) => (
                <button
                  key={i}
                  onClick={() => {
                    const el = sliderRef.current;
                    if (!el) return;
                    const cardWidth = el.children[0]?.offsetWidth ?? el.clientWidth;
                    el.scrollTo({ left: i * (cardWidth + 16), behavior: 'smooth' });
                  }}
                  style={{
                    height: '8px',
                    width: activeSlide === i ? '24px' : '8px',
                    borderRadius: '9999px',
                    background: activeSlide === i ? '#CB6040' : '#D1D5DB',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    padding: 0,
                  }}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
          </div>
        ) : layout === 'tablet' ? (
          /* ── Tablet: 2-column grid ── */
          <div className="px-8">
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-2 gap-8">
                {packages.map((pkg, index) => (
                  <PricingCard
                    key={`${activeTab}-${index}`}
                    pkg={pkg}
                    isVisible={cardsVisible}
                    delay={index * 120}
                    t={t}
                  />
                ))}
              </div>
            </div>
          </div>
        ) : (
          /* ── Desktop: 3-column grid ── */
          <div className="px-12">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-3 gap-8">
                {packages.map((pkg, index) => (
                  <PricingCard
                    key={`${activeTab}-${index}`}
                    pkg={pkg}
                    isVisible={cardsVisible}
                    delay={index * 120}
                    t={t}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PricingCard({ pkg, isVisible, delay, t }) {
  return (
    <div
      style={{
        transitionDelay: `${delay}ms`,
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0) scale(1)' : 'translateY(40px) scale(0.97)',
        transition: 'all 0.6s ease-out',
        height: '100%',
      }}
      className="flex flex-col"
    >
      <div className={`flex flex-col flex-1 bg-white rounded-3xl overflow-hidden transition-all duration-300 hover:-translate-y-1 ${
        pkg.popular
          ? 'shadow-2xl shadow-primary/20 border-2 border-primary/30 ring-1 ring-primary/10'
          : 'shadow-lg hover:shadow-xl border border-gray-100'
      }`}>
        <div className={`h-1.5 w-full ${pkg.popular ? 'bg-gradient-to-r from-primary to-secondary' : 'bg-gradient-to-r from-gray-200 to-gray-300'}`}></div>

        <div className="p-8 flex flex-col flex-1">
          <div className="mb-6">
            {/* Badge now sits inside the card — no clipping, no extra height */}
            {pkg.popular && (
              <span className="inline-block mb-3 px-3 py-1 bg-gradient-to-r from-primary to-secondary text-white text-xs font-bold rounded-full shadow-sm">
                ⭐ {t('popular')}
              </span>
            )}
            <h3 className={`text-xl font-bold mb-2 ${pkg.popular ? 'text-primary' : 'text-gray-900'}`}>
              {pkg.name}
            </h3>
            <p className="text-sm text-gray-500 leading-relaxed">{pkg.tagline}</p>
          </div>

          <div className="mb-8">
            <div className={`text-3xl font-extrabold ${pkg.popular ? 'text-primary' : 'text-gray-900'}`}>
              {pkg.price}
            </div>
            {pkg.monthly && (
              <span className="text-sm text-gray-400 font-medium">{t('perMonth')}</span>
            )}
          </div>

          <div className="flex-1 mb-8">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
              {t('includes')}
            </p>
            <ul className="space-y-3">
              {pkg.features.map((feature, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5 ${pkg.popular ? 'bg-primary/15' : 'bg-gray-100'}`}>
                    <CheckIcon className={`w-3 h-3 ${pkg.popular ? 'text-primary' : 'text-gray-500'}`} />
                  </span>
                  <span className="text-sm text-gray-600 leading-relaxed">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          <Link
            to="/Contact"
            className={`group flex items-center justify-center gap-2 w-full py-3.5 rounded-2xl text-sm font-semibold transition-all duration-300 ${
              pkg.popular
                ? 'bg-gradient-to-r from-primary to-secondary text-white hover:shadow-lg hover:shadow-primary/30 hover:scale-[1.02]'
                : 'bg-gray-50 text-gray-700 border border-gray-200 hover:bg-gray-100 hover:border-gray-300'
            }`}
          >
            {t('cta')}
            <ArrowRightIcon className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </div>
  );
}

function CustomCard({ t, isVisible }) {
  const features = t('custom.features', { returnObjects: true });

  return (
    <div
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(40px)',
        transition: 'all 0.7s ease-out',
      }}
      className="max-w-3xl mx-auto"
    >
      <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl overflow-hidden shadow-2xl">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-64 h-64 bg-primary/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-secondary/20 rounded-full blur-3xl"></div>
        </div>
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary to-secondary"></div>

        <div className="relative p-10 sm:p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center shadow-lg shadow-primary/30">
            <SparklesIcon className="w-8 h-8 text-white" />
          </div>

          <h2 className="text-3xl font-bold text-white mb-3">{t('custom.title')}</h2>
          <p className="text-xl text-primary font-semibold mb-4">{t('custom.tagline')}</p>
          <p className="text-gray-400 leading-relaxed mb-10 max-w-xl mx-auto">{t('custom.description')}</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10 text-left max-w-lg mx-auto">
            {features.map((feature, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                  <CheckIcon className="w-3.5 h-3.5 text-primary" />
                </span>
                <span className="text-sm text-gray-300">{feature}</span>
              </div>
            ))}
          </div>

          <Link
            to="/Contact"
            className="group inline-flex items-center gap-2 px-10 py-4 bg-gradient-to-r from-primary to-secondary text-white font-bold rounded-full hover:shadow-xl hover:shadow-primary/30 transition-all duration-300 hover:scale-105 text-base"
          >
            {t('custom.cta')}
            <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </div>
  );
}
